import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { pauseSub } from 'api/jalapeno';
import { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import { pauseSubIntervalList } from 'data/subscriptionData';
import Button from 'honeydew/Button';
import PausePlanIcon from 'honeydew/icons/MyPlan/PausePlanIcon';
import Select from 'honeydew/Select';
import useGlobalStore from 'hooks/useGlobalStore';
import { trackEvent } from 'utils/analyticsUtils';
import { formatDateString } from 'utils/dateUtils';

function PausePlan({ closeCancelationModal, setCancelationStep, setError, setSuccess, subscription }) {
    const queryClient = useQueryClient();
    const { isMisfitsTheme } = useGlobalStore();

    const [pauseDuration, setPauseDuration] = useState('1 Month');

    const today = new Date();
    const [pauseDate, setPauseDate] = useState(
        formatDateString(
            new Date(today.getFullYear(), today.getMonth() + 1, today.getDate()).toISOString().slice(0, 10),
            true
        )
    );

    const handleSetPauseInterval = (e) => {
        e.preventDefault();
        setPauseDate(
            formatDateString(
                new Date(
                    today.getFullYear(),
                    today.getMonth() + parseInt(e.target.value.charAt(0), 10),
                    today.getDate()
                )
                    .toISOString()
                    .slice(0, 10),
                true
            )
        );
        setPauseDuration(e.target.value);
    };

    const handleConfirmPause = async () => {
        setError('');

        const sendParams = {};
        sendParams.numMonths = pauseDuration;

        const eventProperties = {};
        eventProperties.numberOfMonths = pauseDuration;

        try {
            const response = await pauseSub(subscription.id, sendParams);
            if (response.msg === 'Success') {
                queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                setSuccess('paused');
                trackEvent('plan pause complete', { status: 'PAUSED' });
                closeCancelationModal();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (window.isNativeApp) {
                    window.ReactNativeWebView?.postMessage(JSON.stringify({ reload: true }));
                }
            } else {
                setError('Unable pause plan.');
            }
        } catch {
            setError('Unable pause plan.');
        }
    };

    const continueCancelation = () => {
        setCancelationStep('cancel');
        trackEvent('cancel flow click next');
    };

    return (
        <div className="text-center md:p-40">
            <div className="mx-auto mb-10 w-[75px]">
                <PausePlanIcon />
            </div>
            <h2 className="text-heading-sm mb-10">Pause Plan</h2>
            <p>It&apos;s okay to take a break! You can come back again after 1, 2, or 3 months.</p>
            <div className="mx-auto mt-30 text-left md:mt-40 md:max-w-[380px]">
                <Select
                    labelId="pause-duration-select"
                    id="pauseDurationSelect"
                    label="Pause duration"
                    value={pauseDuration}
                    menuItems={pauseSubIntervalList}
                    onChange={handleSetPauseInterval}
                    isMisfits={isMisfitsTheme}
                    required
                />
            </div>
            <p className="text-body-sm mt-10">Your plan will automatically resume after {pauseDate}.</p>
            <div>
                <div className="text-body-sm mx-auto mb-15 mt-25 md:w-[380px]">
                    <Button size="md" onClick={handleConfirmPause}>
                        Confirm pause
                    </Button>
                </div>
                <button type="button" className="link text-body-sm" onClick={continueCancelation}>
                    Continue to cancel subscription
                </button>
            </div>
        </div>
    );
}

PausePlan.propTypes = {
    closeCancelationModal: PropTypes.func.isRequired,
    setCancelationStep: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    setSuccess: PropTypes.func.isRequired,
    subscription: PropTypes.shape({}),
};

PausePlan.defaultProps = {
    subscription: {},
};

export default PausePlan;
